import { useEffect, useState } from "react";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import useConditionalEffect from "../../../hooks/useConditionalEffect";
import Watermark from "../Watermarks";
import ContactModal from "../contacts/ContactModal";
import { add3DBuildingsLayer } from "../../../utils/layerUtils";
import { registerDrawHandler } from "../../../utils/drawUtils";
import CalculatedResult from "../CalculatedResult";
import MapControls from "./MapControls";
import { useMap } from "../../../hooks/useMap";
import { useDraw } from "../../../hooks/useDraw";
import Controls from "./panel/Controls";
import Legend from "./panel/Legend";
import SwitchSatellite from "./panel/SwitchSatellite";
import styled from "styled-components";
import EmailIcon from "../Icons/EmailIcon";

const Overlay = ({
  isFullScreen,
  handleFullScreen,
  result,
  setResult,
  setDrawMode,
  drawMode,
  toggleDrawModeRef,
  clearRef,
  isCalculated,
  setIsCalculated,
  setCalculatedResult,
  bearing,
  calculatedResult,
}) => {
  const map = useMap();
  const draw = useDraw();
  const [isSatellite, setIsSatellite] = useState(true);
  const [whichMode, setWichMode] = useState("area");
  const [isOpenContact, setIsOpenContact] = useState(false);
  const [isInitContact, setIsInitContact] = useState(false);
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [riskInfo, setRiskInfo] = useState(null);

  useEffect(() => {
    if (riskInfo !== null) {
      const timer = setTimeout(() => {
        if (!isInitContact) setIsOpenContact(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [riskInfo, isInitContact]);

  useConditionalEffect(() => registerDrawHandler(map, draw, setResult, setDrawMode, whichMode, setRiskInfo), [drawMode[whichMode], whichMode]);

  const toggleDrawMode = () => {
    setWichMode("area");
    setDrawMode((prev) => ({
      ...prev,
      area: prev.area === "simple_select" ? "draw_line_string" : "simple_select",
    }));
    if (toggleDrawModeRef.current) {
      toggleDrawModeRef.current("draw_line_string");
    }
  };

  const toggleSatellite = () => {
    setIsSatellite(!isSatellite);
    setIsCalculated(false);
    if (!isSatellite) {
      map.addSource("satellite-source", {
        type: "raster",
        url: "mapbox://mapbox.satellite",
        tileSize: 256,
      });

      map.addLayer({
        id: "satellite-layer",
        type: "raster",
        source: "satellite-source",
        paint: {
          "raster-opacity": 1,
        },
      });

      add3DBuildingsLayer(map);
      map.moveLayer("3d-buildings", "satellite-layer");
    } else {
      map.removeLayer("3d-buildings");
      map.removeLayer("satellite-layer");
      map.removeSource("satellite-source");
    }
    if (map.getLayer("satellite-layer")) {
      map.moveLayer("satellite-layer", "gl-draw-polygon-fill-inactive.cold");
    }
  };

  const clear = () => {
    clearRef.current?.();
  };

  const calculatedHandler = (isOpen) => {
    setIsCalculated(isOpen && result ? true : false);
  };

  const handleOpenMenu = (isOpen) => {
    setIsOpenMenu(isOpen);

    if (!isOpenMenu && window.innerWidth < 660) setIsOpenContact(false);
    if (!isInitContact && isOpenContact) setIsInitContact(true);
  };

  const handleOpenContact = () => {
    setIsOpenContact(!isOpenContact);

    if (!isOpenContact && window.innerWidth < 660) setIsOpenMenu(false);
    if (!isInitContact) setIsInitContact(true);
  };

  return (
    <>
      {!isSatellite && (
        <>
          <Controls toggleDrawMode={toggleDrawMode} drawMode={drawMode} clear={clear} calculatedHandler={calculatedHandler} />
          <Legend />
        </>
      )}
      <SwitchSatellite
        toggleSatellite={toggleSatellite}
        isSatellite={isSatellite}
        toggleDrawModeRef={toggleDrawModeRef}
        drawMode={drawMode}
        setDrawMode={setDrawMode}
        result={result}
        setResult={setResult}
        whichMode={whichMode}
        setWichMode={setWichMode}
        setCalculatedResult={setCalculatedResult}
        calculatedHandler={calculatedHandler}
        isOpenMenu={isOpenMenu}
        handleOpenMenu={handleOpenMenu}
        setRiskInfo={setRiskInfo}
      />
      {isCalculated && result && <CalculatedResult isSatellite={isSatellite} isCalculated={isCalculated} result={result} calculatedResult={calculatedResult} />}
      <MapControls isFullScreen={isFullScreen} handleFullScreen={handleFullScreen} bearing={bearing} />
      {isInitContact && !isOpenContact && (
        <ToogleContactButton onClick={handleOpenContact}>
          <EmailIcon />
        </ToogleContactButton>
      )}
      {isOpenContact && (
        <div className="contact-modal">
          <ContactModal toggleModal={handleOpenContact} {...riskInfo} />
        </div>
      )}
      <Watermark />
    </>
  );
};

const ToogleContactButton = styled.button`
  all: unset;
  position: absolute;
  top: 15px;
  right: 15px;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 8px;
  cursor: pointer;
  z-index: 2;
`;

export default Overlay;
